import axios from 'axios';
import {Contact} from './types';
import logger from './utils/logger';
import { extractApiData, isSuccessResponse } from './utils/apiUtils';
import { clearAuthAndReload, refreshAccessToken } from './api/authRefresh';

// Interceptor: add Bearer token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('xmacna_auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor: handle 401 (expired token)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error.config;
    if (error.response?.status === 401 && request && !request._retry) {
      request._retry = true;
      try {
        const token = await refreshAccessToken(API_URL);
        request.headers.Authorization = `Bearer ${token}`;
        return axios(request);
      } catch {
        clearAuthAndReload();
      }
    }
    return Promise.reject(error);
  }
);

const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://labs.xmacna.ai/strategy-api'
  : 'http://localhost:3003/api';

export const api = {
  /**
   * Fetch all contacts from the API
   */
  async getContacts(): Promise<Contact[]> {
    const response = await axios.get(`${API_URL}/contacts`);
    return response.data;
  },

  /**
   * Fetch a single contact by ID
   */
  async getContact(id: string): Promise<Contact> {
    const response = await axios.get(`${API_URL}/contacts/${id}`);
    return response.data;
  },

  /**
   * Create a new contact
   */
  async createContact(contact: Omit<Contact, 'id'>): Promise<Contact> {
    const response = await axios.post(`${API_URL}/contacts`, contact);
    return response.data;
  },

  /**
   * Update an existing contact
   */
  async updateContact(id: string, contact: Partial<Contact>): Promise<Contact> {
    const response = await axios.put(`${API_URL}/contacts/${id}`, contact);
    return response.data;
  },

  /**
   * Delete a contact
   */
  async deleteContact(id: string): Promise<void> {
    await axios.delete(`${API_URL}/contacts/${id}`);
  },

  /**
   * Dashboard: Get total interactions for today
   */
  async getDashboardTotalInteractions() {
    try {
      logger.log('Calling getDashboardTotalInteractions API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/total-interactions`);
      logger.log('API response - total interactions (RAW):', response);
      
      // Verificar a estrutura da resposta e retornar os dados conforme a estrutura
      if (response.data && (response.data.total_interacoes !== undefined || response.data.data?.total)) {
        // Debug detalhado para rastrear problema de timezone
        logger.log('API response data structure check:');
        logger.log('- Has total_interacoes:', response.data.total_interacoes !== undefined);
        logger.log('- Type of total_interacoes:', typeof response.data.total_interacoes);
        logger.log('- Raw value:', response.data.total_interacoes);
        logger.log('- Parsed value:', parseInt(response.data.total_interacoes) || 0);
        logger.log('- Has data.total:', response.data.data?.total !== undefined);
        
        // Se tiver data.total_interacoes, retorna direto
        if (response.data.total_interacoes !== undefined) {
          return response.data;
        }
        
        // Se estiver encapsulado em data.data.total (formato alternativo)
        if (response.data.data && response.data.data.total) {
          return {
            total_interacoes: response.data.data.total,
            date: response.data.data.date || new Date().toISOString().split('T')[0]
          };
        }
      }
      
      // Caso a estrutura seja inesperada, faz log e retorna um objeto padrão
      logger.warn('Estrutura de resposta inesperada:', response.data);
      return {
        total_interacoes: '0',
        date: new Date().toISOString().split('T')[0]
      };
    } catch (error) {
      logger.error('Error fetching total interactions:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get yesterday's total interactions
   */
  async getYesterdayTotalInteractions() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/yesterday-total-interactions`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching yesterday total interactions:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get yesterday's total interactions until current time
   */
  async getYesterdayTotalInteractionsUntilNow() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/yesterday-total-interactions-until-now`);
      logger.log('API response - yesterday interactions until now:', response);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      logger.warn('Estrutura de resposta inesperada para yesterday-total-interactions-until-now:', response.data);
      return {
        success: false,
        data: {
          total_until_now: 0,
          total_full_day: 0
        }
      };
    } catch (error) {
      logger.error('Error fetching yesterday total interactions until now:', error);
      return {
        success: false,
        error: true,
        data: {
          total_until_now: 0,
          total_full_day: 0
        }
      };
    }
  },

  /**
   * Dashboard: Get last week's same day total interactions
   */
  async getLastWeekTotalInteractions() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/last-week-total-interactions`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching last week total interactions:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get hourly interactions for today
   */
  async getHourlyInteractions() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-interactions`);
      logger.log('API response - hourly interactions:', response);
      
      // Verificar a estrutura da resposta e garantir que retornamos um array
      if (response.data) {
        // Se já for um array, retornamos direto
        if (Array.isArray(response.data)) {
          return response.data;
        }
        
        // Se estiver aninhado em um objeto data
        if (response.data.data && Array.isArray(response.data.data)) {
          return response.data.data;
        }
      }
      
      // Caso a estrutura seja inesperada, faz log e retorna um array vazio
      logger.warn('Estrutura de resposta inesperada para hourly-interactions:', response.data);
      return [];
    } catch (error) {
      logger.error('Error fetching hourly interactions:', error);
      return []; // Retornar array vazio em vez de lançar exceção para evitar quebrar o UI
    }
  },

  /**
   * Dashboard: Get weekly comparison data
   */
  async getWeeklyComparison() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/weekly-comparison`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching weekly comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get weekday comparison data
   */
  async getWeekdayComparison() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/weekday-comparison`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching weekday comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get hourly comparison with yesterday
   */
  async getHourlyComparisonWithYesterday() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-yesterday`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching hourly comparison with yesterday:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get hourly comparison complete data
   */
  async getHourlyComparisonComplete() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-complete`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching complete hourly comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get minute comparison complete data
   */
  async getMinuteComparisonComplete() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/minute-comparison-complete`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching complete minute comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get monthly data
   */
  async getMonthlyData() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/monthly-data`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching monthly data:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get hourly comparison mixed data for bar and line chart
   */
  async getHourlyComparisonMixed() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-mixed`);
      logger.log('API response - hourly mixed comparison:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error fetching hourly mixed comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get hourly comparison data for a specific date
   */
  async getHourlyComparisonForDate(date: string) {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-date`, {
        params: { date }
      });
      logger.log('API response - hourly comparison for date:', date, response.data);
      return response.data;
    } catch (error) {
      logger.error('Error fetching hourly comparison for date:', error);
      return [];
    }
  },

  /**
   * Dashboard: Get top bot clients for today
   */
  getTopBotClients: async () => {
    const response = await axios.get(`${API_URL}/dashboard/top-botclients`);
    return response.data;
  },

  /**
   * Get all FDs with interactions for current month or custom date range
   * This returns ALL FDs without the 150 record limit
   */
  getAllFDsForMonth: async (startDate?: string, endDate?: string) => {
    try {
      let url = `${API_URL}/dashboard/all-fd-month`;
      
      // Adicionar parâmetros de data se fornecidos
      if (startDate && endDate) {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      logger.error('Error fetching all FDs for month:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get top bots by period (day, week, month)
   */
  getTopBots: async (period = 'day') => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/top-bots?period=${period}`);
      return response.data;
    } catch (error) {
      logger.error(`Error fetching top bots for period ${period}:`, error);
      throw error;
    }
  },

  getHourlyComparisonWithLastWeek: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-lastweek`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching hourly comparison with last week:', error);
      throw error;
    }
  },

  getDashboardHourlyComparisonComplete: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-complete`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching complete hourly comparison:', error);
      throw error;
    }
  },

  getDashboardMinuteComparisonComplete: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/minute-comparison-complete`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching complete minute comparison:', error);
      throw error;
    }
  },

  /**
   * Get server time
   */
  getServerTime: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/server-time`);
      logger.log('API response - server time:', response);
      
      // Verificar a estrutura da resposta
      if (response.data) {
        // Se tiver o formato esperado com server_time
        if (response.data.server_time) {
          return response.data;
        }
        
        // Formato alternativo diretamente no data
        if (response.data.formatted_time || response.data.current_hour) {
          return {
            server_time: response.data,
            message: 'Server time retrieved'
          };
        }
      }
      
      // Caso a estrutura seja inesperada, criar um objeto com os dados do cliente
      logger.warn('Estrutura de resposta inesperada para server-time:', response.data);
      const now = new Date();
      return {
        server_time: {
          server_timestamp: now.toISOString(),
          server_date: now.toISOString().split('T')[0],
          current_hour: now.getHours(),
          current_minute: now.getMinutes(),
          formatted_time: now.toISOString()
        },
        message: 'Using client time as fallback'
      };
    } catch (error) {
      logger.error('Error fetching server time:', error);
      // Fallback para o horário do cliente em caso de erro
      const now = new Date();
      return {
        server_time: {
          server_timestamp: now.toISOString(),
          server_date: now.toISOString().split('T')[0],
          current_hour: now.getHours(),
          current_minute: now.getMinutes(),
          formatted_time: now.toISOString()
        },
        message: 'Using client time due to error'
      };
    }
  },

  /**
   * Obter diagnóstico completo do sistema
   */
  getDiagnosticInfo: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/diagnostic`);
      logger.log('API response - system diagnostic:', response);
      
      if (response.data && response.data.diagnostic_info) {
        return response.data;
      }
      
      logger.warn('Estrutura de resposta inesperada para diagnóstico:', response.data);
      return {
        message: 'Formato de resposta de diagnóstico inesperado',
        error: true,
        diagnostic_info: response.data
      };
    } catch (error) {
      logger.error('Error fetching diagnostic info:', error);
      return {
        message: 'Erro ao obter diagnóstico do sistema',
        error: true,
        error_details: error
      };
    }
  },

  /**
   * Debug: Test database connection
   */
  testDatabaseConnection: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/test-db`);
      return response.data;
    } catch (error) {
      logger.error('Error testing database connection:', error);
      throw error;
    }
  },

  /**
   * Debug: Get all interactions without date filtering
   */
  getAllInteractions: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/debug-all-interactions`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching all interactions:', error);
      throw error;
    }
  },

  /**
   * Debug: Get system diagnostic information
   */
  getSystemDiagnostic: async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/system-diagnostic`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching system diagnostic:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get hourly comparison data
   */
  async getHourlyComparison() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/hourly-comparison-complete`);
      return response;
    } catch (error) {
      logger.error('Error fetching hourly comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get minute comparison data
   */
  async getMinuteComparison() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/minute-comparison-complete`);
      return response;
    } catch (error) {
      logger.error('Error fetching minute comparison:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get last week's same day total interactions until now (for week-over-week comparison)
   */
  async getLastWeekTotalInteractionsUntilNow() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/last-week-total-interactions-until-now`);
      logger.log('API response - last week interactions until now:', response);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      logger.warn('Estrutura de resposta inesperada para last-week-total-interactions-until-now:', response.data);
      return {
        success: false,
        data: {
          total_until_now: 0,
          total_full_day: 0
        }
      };
    } catch (error) {
      logger.error('Error fetching last week total interactions until now:', error);
      return {
        success: false,
        error: true,
        data: {
          total_until_now: 0,
          total_full_day: 0
        }
      };
    }
  },

  /**
   * Dashboard: Get top 50 weekly bots
   */
  async getTopBotsWeekly() {
    try {
      logger.log('Calling getTopBotsWeekly API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/top-bots-weekly`);
      logger.log('API response - top weekly bots:', response.data);
      
      // Verificar estrutura da resposta
      if (response.data && response.data.data) {
        return response.data;
      } else {
        logger.warn('Unexpected response structure from top-bots-weekly:', response.data);
        return {
          data: response.data || [],
          date: new Date().toISOString().split('T')[0]
        };
      }
    } catch (error) {
      logger.error('Error fetching top weekly bots:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get top 50 monthly bots
   */
  async getTopBotsMonthly() {
    try {
      logger.log('Calling getTopBotsMonthly API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/top-bots-monthly`);
      logger.log('API response - top monthly bots:', response.data);
      
      // Verificar estrutura da resposta
      if (response.data && response.data.data) {
        return response.data;
      } else {
        logger.warn('Unexpected response structure from top-bots-monthly:', response.data);
        return {
          data: response.data || [],
          date: new Date().toISOString().split('T')[0]
        };
      }
    } catch (error) {
      logger.error('Error fetching top monthly bots:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get daily interactions data for the week
   */
  async getWeeklyDailyData() {
    try {
      logger.log('Calling getWeeklyDailyData API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/weekly-daily-data`);
      logger.log('API response - weekly daily data:', response.data);
      logger.log('API response structure:', JSON.stringify({
        hasData: !!response.data,
        isObject: typeof response.data === 'object',
        isArray: Array.isArray(response.data),
        hasDataProperty: !!(response.data && response.data.data),
        dataPropertyIsArray: !!(response.data && response.data.data && Array.isArray(response.data.data)),
        firstItemSample: response.data && response.data.data && response.data.data.length > 0 
          ? JSON.stringify(response.data.data[0]) 
          : 'No items'
      }, null, 2));
      
      // Verificando por dados da semana anterior
      if (response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const temSemanaAnterior = response.data.data.some((item: any) => 
          item.total_interacoes_anterior !== undefined && item.total_interacoes_anterior !== null);
        logger.log('Dados contêm informação da semana anterior?', temSemanaAnterior);
        
        if (!temSemanaAnterior) {
          logger.warn('Nenhum dado da semana anterior encontrado na resposta!');
          logger.log('Amostra de dados:', JSON.stringify(response.data.data[0], null, 2));
        } else {
          logger.log('Dados da semana anterior encontrados com sucesso.');
          logger.log('Dados da semana anterior (amostra):', response.data.data.map((item: any) => ({
            dia: item.dia,
            atual: item.total_interacoes,
            anterior: item.total_interacoes_anterior
          })));
        }
      }
      
      // Verificar estrutura da resposta
      if (response.data && response.data.data) {
        return response.data;
      } else {
        logger.warn('Unexpected response structure from weekly-daily-data:', response.data);
        return {
          data: response.data || [], 
          date: new Date().toISOString().split('T')[0]
        };
      }
    } catch (error) {
      logger.error('Error fetching weekly daily data:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get daily interactions data for the month
   */
  async getMonthlyDailyData() {
    try {
      logger.log('Calling getMonthlyDailyData API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/monthly-daily-data`);
      logger.log('API response - monthly daily data:', response.data);
      logger.log('API response structure:', JSON.stringify({
        hasData: !!response.data,
        isObject: typeof response.data === 'object',
        isArray: Array.isArray(response.data),
        hasDataProperty: !!(response.data && response.data.data),
        dataPropertyIsArray: !!(response.data && response.data.data && Array.isArray(response.data.data)),
        firstItemSample: response.data && response.data.data && response.data.data.length > 0 
          ? JSON.stringify(response.data.data[0]) 
          : 'No items'
      }, null, 2));
      
      // Verificar estrutura da resposta
      if (response.data && response.data.data) {
        return response.data;
      } else {
        logger.warn('Unexpected response structure from monthly-daily-data:', response.data);
        return {
          data: response.data || [], 
          date: new Date().toISOString().split('T')[0]
        };
      }
    } catch (error) {
      logger.error('Error fetching monthly daily data:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get today's total interactions until last complete hour
   */
  async getTodayTotalInteractionsUntilNow() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/today-total-interactions-until-now`);
      logger.log('API response - today interactions until now:', response);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      logger.warn('Estrutura de resposta inesperada para today-total-interactions-until-now:', response.data);
      return {
        success: false,
        data: {
          total_until_now: 0,
          total_full_day: 0
        }
      };
    } catch (error) {
      logger.error('Error fetching today total interactions until now:', error);
      return {
        success: false,
        error: true,
        data: {
          total_until_now: 0,
          total_full_day: 0
        }
      };
    }
  },

  /**
   * Dashboard: Get current month's total interactions with monetary value
   */
  async getCurrentMonthTotalInteractions() {
    try {
      logger.log('Calling getCurrentMonthTotalInteractions API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/current-month-total-interactions`);
      logger.log('API response - current month total interactions:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error fetching current month total interactions:', error);
      throw error;
    }
  },

  /**
   * Dashboard: Get weekly total interactions
   */
  async getWeeklyTotalInteractions() {
    try {
      logger.log('Calling getWeeklyTotalInteractions API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/weekly-total-interactions`);
      logger.log('API response - weekly total interactions:', response.data);
      
      if (response.data && response.data.total_interacoes !== undefined) {
        return response.data;
      }
      
      logger.warn('Estrutura de resposta inesperada para weekly-total-interactions:', response.data);
      return {
        total_interacoes: 0,
        week_start: '',
        week_end: ''
      };
    } catch (error) {
      logger.error('Error fetching weekly total interactions:', error);
      return {
        total_interacoes: 0,
        week_start: '',
        week_end: ''
      };
    }
  },

  /**
   * Dashboard: Get unified comparison data
   */
  async getUnifiedComparisonData() {
    try {
      logger.log('Calling getUnifiedComparisonData API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/unified-comparison-data`);
      
      if (response.data && response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Ensure all numeric values are properly parsed as numbers
        const ensureNumber = (value: any) => {
          if (typeof value === 'string') return parseFloat(value) || 0;
          if (typeof value === 'number') return value;
          return 0;
        };
        
        // Normalize the response data
        const normalizedData = {
          today_total: ensureNumber(data.today_total),
          today_until_hour: ensureNumber(data.today_until_hour),
          yesterday_total: ensureNumber(data.yesterday_total),
          yesterday_until_hour: ensureNumber(data.yesterday_until_hour),
          last_week_total: ensureNumber(data.last_week_total),
          last_week_until_hour: ensureNumber(data.last_week_until_hour),
          weekly_total: ensureNumber(data.weekly_total),
          daily_delta: ensureNumber(data.daily_delta),
          daily_delta_percentage: ensureNumber(data.daily_delta_percentage),
          weekly_delta: ensureNumber(data.weekly_delta),
          weekly_delta_percentage: ensureNumber(data.weekly_delta_percentage),
          current_hour: ensureNumber(data.current_hour),
          current_minute: ensureNumber(data.current_minute),
          day_of_week: ensureNumber(data.day_of_week),
          reference_date: data.reference_date
        };
        
        logger.log('Normalized comparison data:', normalizedData);
        return normalizedData;
      } else {
        logger.error('Error in unified comparison data response:', response.data);
        return null;
      }
    } catch (error) {
      logger.error('Error fetching unified comparison data:', error);
      return null;
    }
  },

  /**
   * Get inactive bots data for monitoring
   */
  async getInactiveBots() {
    try {
      logger.log('Calling getInactiveBots API endpoint...');
      const response = await axios.get(`${API_URL}/dashboard/inactive-bots`);
      logger.log('API response - inactive bots:', response.data);
      
      if (response.data && response.data.success) {
        return response.data;
      } else {
        logger.error('Error in inactive bots response:', response.data);
        return null;
      }
    } catch (error) {
      logger.error('Error fetching inactive bots data:', error);
      return null;
    }
  },

  /**
   * Get inactive bots report as CSV
   * @param {string} period - Time period for report: 'daily', 'weekly', or 'monthly'
   */
  async exportInactiveBotsCSV(period: 'daily' | 'weekly' | 'monthly') {
    try {
      logger.log(`Exporting inactive bots CSV for period: ${period}`);
      const response = await axios.get(`${API_URL}/dashboard/export-inactive-bots-csv`, {
        params: { period },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bots-inativos-${period}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      logger.error('Error exporting inactive bots CSV:', error);
      return false;
    }
  },

  /**
   * CONSOLIDATED ENDPOINT - Initial Load V2 (Optimized)
   * Returns ALL dashboard data in a single optimized request (~2s vs 40s)
   * Uses created_at_brasilia index for 17x faster queries
   */
  async getInitialDashboardData() {
    try {
      const response = await axios.get(`${API_URL}/dashboard/initial-load-v2`);
      if (response.data?.success) {
        return response.data;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
      return null;
    }
  },

  /**
   * Get consolidated daily report data
   * Returns FDs with drops, hibernating FDs, top performers with trends, and volume analysis
   */
  async getConsolidatedReport() {
    try {
      logger.log('Calling getConsolidatedReport API endpoint...');
      const response = await axios.get(`${API_URL}/alerts/consolidated-report`);
      logger.log('API response - consolidated report:', response.data);

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }

      logger.warn('Unexpected response structure from consolidated-report:', response.data);
      return null;
    } catch (error) {
      logger.error('Error fetching consolidated report:', error);
      return null;
    }
  },

  async getPartenonDashboard() {
    try {
      const response = await axios.get(`${API_URL}/partenon-dash/initial-load`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching partenon dashboard:', error);
      return null;
    }
  },
};
